import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as R from 'remeda';
import { BehaviorSubject, combineLatest, forkJoin, interval, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  mapTo,
  shareReplay,
  switchMap,
  switchMapTo,
  take,
  tap,
} from 'rxjs/operators';
import { getFracName } from 'src/app/ui-components/pipes/frac-name.pipe';
import { environment } from '~environments/environment';
import {
  AutoDispatchDisableResponse,
  AutopilotLevel,
  BoxPickupSite,
  CreateBoxPickupSiteRequest,
  DiversionInfo,
  Frac,
  FracCluster,
  FracGraphSocketUpdate,
  FracStage,
  FracToCopy,
  FracVendorAllocationStats,
  GhostSand,
  GhostSandPartial,
  LmoFracListSummary,
  RerouteOrderRequestData,
  StageMeshVolumeUpdate,
  StorageCurrentStock,
  VendorDetail,
} from '~lmo/models/frac.model';
import { AuthService } from '~services/auth.service';
import { ErrorHandlingService } from '~services/error-handling.service';
import { FeatureFlagService } from '~services/feature-flag.service';
import { RouterStateService } from '~services/router-state.service';
import { TrailerType } from '~v2Models/well.model';
import * as fromRouterConstants from '../lmo-routing.constants';

const prettyNames: Record<AutopilotLevel, string> = {
  full: 'Auto Ordering and Auto Assignment have been turned on for ',
  off: 'Autopilot is now off for ',
  semi: 'Auto Ordering has been turned on for ',
};

export interface NptReport {
  meshId: number;
  currentStock: number;
  scheduledPumpVolume: number;
}

@Injectable({
  providedIn: 'root',
})
export class LmoFracsService {
  private unsubscribe$ = new Subject<void>();

  private fracListLoading = false;
  private archivedFracListLoading = false;
  private fracToCopyListLoading = false;
  private boxPickUpSiteListLoading = false;
  private fracs$$: BehaviorSubject<Record<number, Frac>> = new BehaviorSubject<Record<number, Frac>>({});
  private archivedFracs$$: BehaviorSubject<Record<number, Frac>> = new BehaviorSubject<Record<number, Frac>>({});
  private fracsOptionsToCopy$$: BehaviorSubject<Record<number, FracToCopy>> = new BehaviorSubject<
    Record<number, FracToCopy>
  >({});
  private boxPickUpSites$$: BehaviorSubject<Record<number, BoxPickupSite>> = new BehaviorSubject<
    Record<number, BoxPickupSite>
  >({});
  private fracSummaries$$: BehaviorSubject<LmoFracListSummary[]> = new BehaviorSubject([]);
  private currentFrac$$: BehaviorSubject<Frac> = new BehaviorSubject<Frac>(null);
  private trailerTypes$$: BehaviorSubject<TrailerType[]> = new BehaviorSubject([]);
  // Since this comes in as a separate socket update, we are going to store and consume this separately from the frac for speed.
  private currentFracGraph$$: BehaviorSubject<FracGraphSocketUpdate> = new BehaviorSubject(null);
  private currentFracStageSelected$$: BehaviorSubject<FracStage> = new BehaviorSubject(null);
  private currentFracCurrentStage$$: BehaviorSubject<FracStage> = new BehaviorSubject(null);
  private fracSummaryListLoading$$ = new BehaviorSubject(false);
  private fracVendorAllocationStatsLoading$$ = new BehaviorSubject(false);

  public fracSummaryListLoading$ = this.fracSummaryListLoading$$.pipe(shareReplay(1));
  public fracVendorAllocationStatsLoading$ = this.fracVendorAllocationStatsLoading$$.pipe(shareReplay(1));
  public get fracs$(): Observable<Frac[]> {
    this.loadFracList();
    return this.fracs$$
      .asObservable()
      .pipe(map((fracsRecord) => Object.keys(fracsRecord).map((fracId) => fracsRecord[fracId])));
  }

  public get archivedFracs$(): Observable<Frac[]> {
    this.loadArchivedFracList();
    return this.archivedFracs$$
      .asObservable()
      .pipe(
        map((archivedFracsRecord) =>
          Object.keys(archivedFracsRecord).map((archivedFracId) => archivedFracsRecord[archivedFracId]),
        ),
      );
  }

  public get fracSummaries$(): Observable<LmoFracListSummary[]> {
    this.loadFracSummaryList();
    return this.fracSummaries$$.asObservable();
  }

  public get currentFrac$(): Observable<Frac> {
    return this.currentFrac$$.asObservable();
  }

  public get trailerTypes$(): Observable<TrailerType[]> {
    this.loadTrailerTypes();
    return this.trailerTypes$$.asObservable();
  }

  public get currentFracGraph$(): Observable<FracGraphSocketUpdate> {
    return this.currentFracGraph$$.asObservable();
  }

  public get currentFracCurrentStage$(): Observable<FracStage> {
    return this.currentFracCurrentStage$$.asObservable();
  }

  public get currentFracStageSelected$(): Observable<FracStage> {
    return this.currentFracStageSelected$$.asObservable();
  }

  public getNameByFracId$(fracId: number): Observable<string> {
    return this.fracs$$.asObservable().pipe(
      map((fracs) => {
        const frac = fracs[fracId];
        return getFracName(frac);
      }),
    );
  }

  public getFracClusterIdByFracId$(fracId: number): Observable<number> {
    return this.fracs$$.asObservable().pipe(
      map((fracs) => {
        const frac = fracs[fracId];
        return frac.cluster.id;
      }),
    );
  }

  public get fracsOptionsToCopy$(): Observable<FracToCopy[]> {
    this.loadFracOptionsToCopyList();
    return this.fracsOptionsToCopy$$
      .asObservable()
      .pipe(map((fracsRecord) => Object.keys(fracsRecord).map((fracId) => fracsRecord[fracId])));
  }

  public get boxPickupSites$(): Observable<BoxPickupSite[]> {
    this.loadBoxPickupSites();
    return this.boxPickUpSites$$
      .asObservable()
      .pipe(map((sitesRecord) => Object.keys(sitesRecord).map((siteId) => sitesRecord[siteId])));
  }

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private routerState: RouterStateService,
    private errorService: ErrorHandlingService,
    private snackBar: MatSnackBar,
    private featureFlagService: FeatureFlagService,
  ) {
    this.logout = this.logout.bind(this);
    this.setupAuthListener();
    this.subscribeToRouterFracIdChanges();
    this.subscribeToRouterStageNumberChanges();
    this.loadFracList();
    this.loadFracSummaryList();
    this.fracListPolling();
    this.getCurrentFrac();
  }

  public getCurrentFrac(): void {
    this.routerState.routerState$
      .pipe(
        map((state) => state.params[fromRouterConstants.FRAC_ID]), // Extract fracId from route params
        distinctUntilChanged(), // Ensure API is called only when fracId changes
        switchMap((fracId) => {
          if (!fracId) {
            // If fracId is null or undefined, set currentFrac$$ to null and return an empty observable
            this.currentFrac$$.next(null);
            return of(null); // Return an observable that emits `null` to terminate the stream
          }
          // Call the API when fracId changes
          return this.http.get<Frac>(`${environment.api}/frac/${fracId}`);
        }),
      )
      .subscribe(
        (frac) => {
          if (frac) {
            this.currentFrac$$.next(frac);
          } else {
            this.currentFrac$$.next(null);
          }
        },
        (error) => {
          this.currentFrac$$.next(null); // Handle the case of an API error
        },
      );
  }

  public getRecordsForCurrentFrac$(): Observable<string> {
    if (this.currentFrac$$.value) {
      return this.http.get<string>(`${environment.api}/frac/${this.currentFrac$$.value.id}/csvurl`);
    }
    return of(null);
  }

  public startBolImageExportForCurrentFrac$(
    startYear: number,
    startMonth: number,
    startDay: number,
    endYear: number,
    endMonth: number,
    endDay: number,
  ): Observable<string> {
    if (this.currentFrac$$.value) {
      return this.http
        .get<{ message: string }>(`${environment.api}/frac/${this.currentFrac$$.value.id}/bol_image_export`, {
          params: {
            endDay: `${endDay}`,
            endMonth: `${endMonth}`,
            endYear: `${endYear}`,
            startDay: `${startDay}`,
            startMonth: `${startMonth}`,
            startYear: `${startYear}`,
          },
        })
        .pipe(map((resp) => resp.message));
    }
    return of(null);
  }

  public getAutoDispatchState$(): Observable<AutoDispatchDisableResponse> {
    return this.currentFrac$$.pipe(
      take(1),
      switchMap((frac) => {
        if (!frac) {
          return of(null);
        }
        return this.http.get<boolean>(`${environment.api}/frac/${frac.id}/auto_dispatch_status`);
      }),
    );
  }

  public toggleAutoDispatch$(autoDispatch: boolean): Observable<AutoDispatchDisableResponse> {
    return this.currentFrac$$.pipe(
      take(1),
      switchMap((frac) => {
        if (!frac) {
          return of(null);
        }
        return this.http.post(`${environment.api}/frac/${frac.id}/toggle_auto_dispatch`, { disable: autoDispatch });
      }),
    );
  }

  public changeAutopilotLevelForCurrentFrac$(level: AutopilotLevel): Observable<boolean> {
    if (this.currentFrac$$.value) {
      return this.http
        .patch(`${environment.api}/frac_cluster/${this.currentFrac$$.value.cluster.id}/autopilot`, {
          level,
        })
        .pipe(
          tap(() => {
            const updatedFracCluster: FracCluster = {
              ...this.currentFrac$$.value.cluster,
              autopilotLevel: level,
            };
            this.currentFrac$$.next({
              ...this.currentFrac$$.value,
              cluster: updatedFracCluster,
            });
          }),
          tap(() => {
            const baseMesasge = prettyNames[level];
            const message = `${baseMesasge}${getFracName(this.currentFrac$$.value)}`;
            this.snackBar.open(message, null, { duration: 5000 });
            this.updateFracById(this.currentFrac$$.value.id, this.currentFrac$$.value);
          }),
          map((response) => {
            return true;
          }),
          catchError((error) => {
            this.errorService.showError(error);
            return of(false);
          }),
        );
    }
    return of(false);
  }

  private updateFracById(id, updatedFrac) {
    const allFracs = this.fracs$$.getValue();
    allFracs[id] = { ...updatedFrac };
    this.fracs$$.next(allFracs);
  }

  public updateStage$(stageMeshVolumes: StageMeshVolumeUpdate[]): Observable<boolean> {
    if (!this.currentFrac$$.value || !this.currentFracStageSelected$$.value) {
      this.errorService.showError('No active Frac or Frac Stage');
      return of(false);
    }
    const fracId = this.currentFrac$$.value.id;
    const stageNumber = this.currentFracStageSelected$$.value.stageNumber;

    interface FracStageUpdateReq {
      fracId: number;
      stageNumber: number;
      stageMeshVolumes: StageMeshVolumeUpdate[];
    }

    const fracStageUpdateReq: FracStageUpdateReq = {
      fracId,
      stageMeshVolumes,
      stageNumber,
    };

    return this.http.put(`${environment.api}/update_stage`, fracStageUpdateReq).pipe(
      tap((updatedStage: FracStage) => {
        const stagesToUpdate = [...this.currentFrac$$.value.fracStages];
        const indexOfUpdatedStage = stagesToUpdate.findIndex((stage) => stage.stageNumber === updatedStage.stageNumber);
        if (indexOfUpdatedStage !== -1) {
          stagesToUpdate.splice(indexOfUpdatedStage, 1, updatedStage);
          this.currentFrac$$.next({
            ...this.currentFrac$$.value,
            fracStages: stagesToUpdate,
          });
        }
      }),
      tap(() => {
        this.snackBar.open('Stage Updated', null, { duration: 5000 });
      }),
      mapTo(true),
      catchError((error) => {
        this.errorService.showError(error);
        return of(false);
      }),
    );
  }

  public completeStage$(
    stageMeshVolumes: StageMeshVolumeUpdate[],
    storageCurrentStocks: StorageCurrentStock[],
    nptReports: NptReport[],
  ): Observable<boolean> {
    if (!this.currentFrac$$.value) {
      this.errorService.showError('No active Frac or Frac Stage');
      return of(false);
    }
    const fracId = this.currentFrac$$.value.id;
    const currentStage = this.currentFrac$$.value.currentStage;

    interface FracStageCompleteReq {
      stageMeshVolumes: StageMeshVolumeUpdate[];
      storageCurrentStocks: StorageCurrentStock[];
      nptReports: NptReport[];
    }

    const fracStageCompleteReq: FracStageCompleteReq = {
      nptReports,
      stageMeshVolumes,
      storageCurrentStocks,
    };

    return this.http
      .post(`${environment.api}/frac/${fracId}/stage/${currentStage}/complete`, fracStageCompleteReq)
      .pipe(
        tap((updatedStage: FracStage) => {
          const stagesToUpdate = [...this.currentFrac$$.value.fracStages];
          const indexOfUpdatedStage = stagesToUpdate.findIndex(
            (stage) => stage.stageNumber === updatedStage.stageNumber,
          );
          if (indexOfUpdatedStage !== -1) {
            stagesToUpdate.splice(indexOfUpdatedStage, 1, updatedStage);
            this.currentFrac$$.next({
              ...this.currentFrac$$.value,
              currentStage: this.currentFrac$$.value.currentStage + 1,
              fracStages: stagesToUpdate,
            });
          }
        }),
        tap(() => {
          this.snackBar.open(`Stage ${currentStage} Completed`, null, { duration: 5000 });
        }),
        tap(() => {
          this.reloadCurrentFrac$().subscribe();
        }),
        mapTo(true),
        catchError((error) => {
          this.errorService.showError(error);
          return of(false);
        }),
      );
  }

  public deleteStage$(): Observable<boolean> {
    if (!this.currentFrac$$.value) {
      this.errorService.showError('No active Frac or Frac Stage');
      return of(false);
    }
    const fracId = this.currentFrac$$.value.id;

    return this.http.delete(`${environment.api}/frac/${fracId}/stage/last`).pipe(
      tap((updatedFrac: Frac) => {
        this.currentFrac$$.next(updatedFrac);
      }),
      tap(() => {
        this.snackBar.open('Stage Deleted', null, { duration: 5000 });
      }),
      tap((frac) => {
        if (frac.completed) {
          this.currentFracCurrentStage$$.next(null);
        } else {
          this.currentFracCurrentStage$$.next(getStageFromFrac(frac, frac.currentStage));
        }
      }),
      mapTo(true),
      catchError((error) => {
        this.errorService.showError(error);
        return of(false);
      }),
    );
  }

  public fetchCurrentFrac() {
    const fracId = this.currentFrac$$.value.id;
    this.http.get<Frac>(`${environment.api}/frac/${fracId}`).subscribe((frac) => {
      this.currentFrac$$.next(frac);
      const fakeSocketUpdate: FracGraphSocketUpdate = {
        fracId: frac.id,
        npts: frac.npt || [],
        orderRecommendations: frac.recommendations || [],
        rerouteRecommendations: [],
        storages: R.sortBy(frac.fracStorages || [], (fracStorage) => fracStorage.mesh.preferredOrder),
      };
      this.currentFracGraph$$.next(fakeSocketUpdate);
    });
  }

  public updateGhostSands$(ghostSandsPartial: GhostSandPartial[]): Observable<boolean> {
    if (!this.currentFrac$$.value) {
      this.errorService.showError('No active Frac or Frac Stage');
      return of(false);
    }
    const fracId = this.currentFrac$$.value.id;
    const ghostSands: GhostSand[] = ghostSandsPartial.map((ghostSand) => ({
      ...ghostSand,
      fracId,
    }));
    const updates: Observable<null>[] = ghostSands.map((ghostSand) =>
      this.http.post<null>(`${environment.api}/sand/storage/${ghostSand.storageId}/ghost_sand`, ghostSand),
    );
    return forkJoin(updates).pipe(
      switchMap(() => this.reloadCurrentFrac$()),
      tap(() => {
        this.snackBar.open('Current Stock Updated', null, { duration: 5000 });
        this.fetchCurrentFrac();
      }),
      mapTo(true),
      catchError((error) => {
        this.errorService.showError(error);
        return of(false);
      }),
    );
  }

  private setupAuthListener() {
    this.authService.isLoggedIn$
      .pipe(
        distinctUntilChanged(),
        filter((isLoggedIn) => !isLoggedIn),
      )
      .subscribe(this.logout);
  }

  private loadFracList() {
    if (!this.fracListLoading) {
      this.fracListLoading = true;
      this.http.get<Frac[]>(`${environment.api}/frac`).subscribe(
        (fracs) => {
          this.fracListLoading = false;
          const fracsRecord = fracs.reduce((record, frac) => {
            record[frac.id] = frac;
            return record;
          }, {});
          this.fracs$$.next(fracsRecord);
        },
        (error) => {
          this.fracListLoading = false;
        },
      );
    }
  }

  private loadArchivedFracList() {
    if (!this.archivedFracListLoading) {
      this.archivedFracListLoading = true;
      this.http.get<Frac[]>(`${environment.api}/frac/archived`).subscribe(
        (archivedFracs) => {
          this.archivedFracListLoading = false;
          const archivedFracsRecord = archivedFracs.reduce((record, archivedFrac) => {
            record[archivedFrac.id] = archivedFrac;
            return record;
          }, {});
          this.archivedFracs$$.next(archivedFracsRecord);
        },
        (error) => {
          this.archivedFracListLoading = false;
        },
      );
    }
  }

  private loadFracSummaryList() {
    if (!this.fracSummaryListLoading$$.value) {
      this.fracSummaryListLoading$$.next(true);
      this.http.get<LmoFracListSummary[]>(`${environment.api}/lmo_frac_summaries`).subscribe(
        (summaries) => {
          this.fracSummaryListLoading$$.next(false);
          const fracs = this.sortFracSummaries(summaries);
          this.fracVendorAllocationStatsLoading$$.next(true);
          this.fracSummaries$$.next(fracs);
          this.loadAndMergeFracVendorAllocationStats(fracs);
        },
        (error) => {
          this.fracSummaryListLoading$$.next(false);
        },
      );
    }
  }

  private loadAndMergeFracVendorAllocationStats(currentSummaries: LmoFracListSummary[]) {
    this.http
      .get<FracVendorAllocationStats[]>(`${environment.api}/lmo_frac_vendor_allocation_stats`)
      .pipe(
        catchError(() => of([] as FracVendorAllocationStats[])),
        finalize(() => this.fracVendorAllocationStatsLoading$$.next(false)),
      )
      .subscribe((stats) => {
        if (!stats.length) {
          return;
        }
        const statsByFracId = new Map(stats.map((stat) => [stat.fracId, stat]));
        const fracs = this.sortFracSummaries(
          currentSummaries.map((summary) =>
            this.mergeFracVendorAllocationStats(summary, statsByFracId.get(summary.lmoFracSummary.id)),
          ),
        );
        this.fracSummaries$$.next(fracs);
      });
  }

  private sortFracSummaries(summaries: LmoFracListSummary[]): LmoFracListSummary[] {
    const fracs = [...summaries];
    fracs.sort((a, b) =>
      (a.lmoFracSummary.name || '')
        .toLocaleLowerCase()
        .localeCompare((b.lmoFracSummary.name || '').toLocaleLowerCase()),
    );
    fracs.forEach((frac) =>
      frac.vendorDetails.sort((a, b) =>
        a.truckingVendorName.toLocaleLowerCase().localeCompare(b.truckingVendorName.toLocaleLowerCase()),
      ),
    );
    return fracs;
  }

  private mergeFracVendorAllocationStats(
    summary: LmoFracListSummary,
    stats?: FracVendorAllocationStats,
  ): LmoFracListSummary {
    if (!stats) {
      return summary;
    }
    const byId = new Map(stats.vendors.map((vendor) => [String(vendor.id), vendor]));
    const mergeVendor = (vendor: VendorDetail): VendorDetail => {
      const stat = byId.get(String(vendor.fctvId));
      if (!stat) {
        return vendor;
      }
      return {
        ...vendor,
        currentTruckPoolAllocationPercent: stat.currentTruckPoolAllocationPercent,
        inProgressOrderCount: stat.inProgressOrderCount,
      };
    };
    return {
      ...summary,
      lmoFracSummary: {
        ...summary.lmoFracSummary,
        inProgressOrderCount: stats.inProgressOrderCount,
      },
      vendorDetails: (summary.vendorDetails || []).map(mergeVendor),
      overflowVendorDetails: (summary.overflowVendorDetails || []).map(mergeVendor),
    };
  }

  private loadTrailerTypes() {
    this.http.get<TrailerType[]>(`${environment.api}/trailer_types`).subscribe((trailerTypes) => {
      this.trailerTypes$$.next(trailerTypes);
    });
  }

  private subscribeToRouterFracIdChanges() {
    this.routerState
      .listenForParamChange$(fromRouterConstants.FRAC_ID)
      .pipe(
        distinctUntilChanged(), // Ensures the observable chain is stopped when unsubscribe$ emits
        filter((fracId) => !!fracId), // Skip any falsy fracId values
        switchMap((fracId) => {
          const localCopyOfFrac: Frac = this.fracs$$.value[fracId];
          if (localCopyOfFrac) {
            this.currentFrac$$.next(localCopyOfFrac);

            if (
              localCopyOfFrac.fracStorages.every(
                (fracStorage) => fracStorage.currentStocks && fracStorage.currentStocks.length > 0,
              )
            ) {
              const fakeSocketUpdate: FracGraphSocketUpdate = {
                fracId: localCopyOfFrac.id,
                npts: localCopyOfFrac.npt || [],
                orderRecommendations: localCopyOfFrac.recommendations || [],
                rerouteRecommendations: [],
                storages: R.sortBy(
                  localCopyOfFrac.fracStorages || [],
                  (fracStorage) => fracStorage.mesh.preferredOrder,
                ),
              };
              this.currentFracGraph$$.next(fakeSocketUpdate);
            } else {
              this.currentFracGraph$$.next(null);
            }
          } else {
            this.currentFrac$$.next(null);
            this.currentFracGraph$$.next(null);
          }

          // Return an observable from the HTTP request
          return this.http.get<Frac>(`${environment.api}/frac/${fracId}`);
        }),
      )
      .subscribe((frac) => {
        this.currentFrac$$.next(frac);
        const fakeSocketUpdate: FracGraphSocketUpdate = {
          fracId: frac.id,
          npts: frac.npt || [],
          orderRecommendations: frac.recommendations || [],
          rerouteRecommendations: [],
          storages: R.sortBy(frac.fracStorages || [], (fracStorage) => fracStorage.mesh.preferredOrder),
        };
        this.currentFracGraph$$.next(fakeSocketUpdate);

        if (frac.completed) {
          this.currentFracCurrentStage$$.next(null);
        } else {
          this.currentFracCurrentStage$$.next(getStageFromFrac(frac, frac.currentStage));
        }
      });
  }

  public unsubscribe() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private subscribeToRouterStageNumberChanges(): void {
    combineLatest([this.currentFrac$, this.routerState.listenForParamChange$(fromRouterConstants.STAGE_NUMBER)])
      .pipe(
        filter((result) => result.every((a) => !!a)),
        map(([frac, stageNumber]) => [frac, +stageNumber] as [Frac, number]),
      )
      .subscribe(([frac, stageNumber]) => {
        this.currentFracStageSelected$$.next(getStageFromFrac(frac, stageNumber));
      });
  }

  public reloadCurrentFrac$(): Observable<boolean> {
    if (this.currentFrac$$.value) {
      return this.http.get<Frac>(`${environment.api}/frac/${this.currentFrac$$.value.id}`).pipe(
        tap((frac) => {
          this.currentFrac$$.next(frac);
        }),
        tap((frac) => {
          if (frac.completed) {
            this.currentFracCurrentStage$$.next(null);
          } else {
            this.currentFracCurrentStage$$.next(getStageFromFrac(frac, frac.currentStage));
          }
        }),
        map(() => true),
        catchError((error) => {
          this.errorService.showError(error);
          return of(false);
        }),
      );
    }
    return of(false);
  }

  private logout() {
    this.fracSummaries$$.next([]);
    this.fracVendorAllocationStatsLoading$$.next(false);
    this.fracs$$.next({});
    this.currentFrac$$.next(null);
    this.currentFracGraph$$.next(null);
    this.currentFracStageSelected$$.next(null);
    this.currentFracCurrentStage$$.next(null);
  }

  private fracListPolling() {
    this.routerState.routerState$
      .pipe(
        filter((state) => !!state),
        map((state) => state.url),
        switchMap((url) => {
          if (url.endsWith('lmo/frac/list')) {
            return interval(5 * 60 * 1000).pipe(
              switchMapTo(this.featureFlagService.isFlagActive('fracListPolling').pipe(take(1))),
              tap((isFlagActive) => {
                if (isFlagActive) {
                  this.loadFracSummaryList();
                }
              }),
            );
          } else {
            return of(null);
          }
        }),
      )
      .subscribe();
  }

  public toggleAutoPurchaseOrderAssignment$(autoPurchaseOrderAssignment: boolean): Observable<boolean> {
    if (!this.currentFrac$$.value) {
      this.errorService.showError('No active Frac or Frac Stage');
      return of(false);
    }

    const body = { autoPurchaseOrderAssignment };
    return this.http
      .patch(
        `${environment.api}/auto_procurement/fracs/${this.currentFrac$$.value.id}/toggle_auto_purchase_order_assignment`,
        body,
      )
      .pipe(
        mapTo(true),
        catchError((error) => {
          this.errorService.showError(error);
          return of(false);
        }),
        tap(() => this.reloadCurrentFrac$().subscribe()),
      );
  }

  private loadFracOptionsToCopyList() {
    if (!this.fracToCopyListLoading) {
      this.fracToCopyListLoading = true;
      this.http.get<FracToCopy[]>(`${environment.api}/frac/box_pickup/copy_options`).subscribe(
        (fracs) => {
          this.fracToCopyListLoading = false;
          const fracsRecord = fracs.reduce((record, frac) => {
            record[frac.id] = frac;
            return record;
          }, {});
          this.fracsOptionsToCopy$$.next(fracsRecord);
        },
        (error) => {
          this.fracToCopyListLoading = false;
        },
      );
    }
  }

  private loadBoxPickupSites() {
    if (!this.boxPickUpSiteListLoading) {
      this.boxPickUpSiteListLoading = true;
      this.http.get<BoxPickupSite[]>(`${environment.api}/frac/box_pickup_sites`).subscribe(
        (sites) => {
          this.boxPickUpSiteListLoading = false;
          const sitesRecord = sites.reduce((record, site) => {
            record[site.id] = site;
            return record;
          }, {});
          this.boxPickUpSites$$.next(sitesRecord);
        },
        (error) => {
          this.boxPickUpSiteListLoading = false;
        },
      );
    }
  }

  public getPossibleDiversions$(fracId: number, meshId: number): Observable<DiversionInfo[]> {
    return this.http.get(`${environment.api}/frac/${fracId}/mesh/${meshId}/possible_diversions`) as Observable<
      DiversionInfo[]
    >;
  }

  public rerouteOrders$(rerouteOrderRequestData: RerouteOrderRequestData[]): Observable<any> {
    return this.http.post(`${environment.api}/reroute_orders`, rerouteOrderRequestData);
  }

  public createBoxPickupSite$(requestData: CreateBoxPickupSiteRequest): Observable<any> {
    return this.http.post(`${environment.api}/frac/create_box_pickup_site`, requestData);
  }

  public updateBoxPickupSite$(requestData: CreateBoxPickupSiteRequest): Observable<any> {
    return this.http.put(`${environment.api}/frac/update_box_pickup_site`, requestData);
  }

  public archiveBoxPickupSite$(id: number): Observable<any> {
    return this.http.put(`${environment.api}/frac/archive_box_pickup_site`, id);
  }
}

function getStageFromFrac(frac: Frac, stageNumber: number): FracStage {
  if (!frac.fracStages) {
    return null;
  }
  return frac.fracStages.find((stage) => stage.stageNumber === stageNumber);
}
